import type { PointerEvent } from 'react';
import type { ICellRendererParams, ColDef } from 'ag-grid-community';
import { FreeTextDropdownEditor } from './free-text-dropdown-editor';
import { isRowEmpty, type SampleSheetRowDraft, type SamplingPointDraftState } from './sample-sheet-data';

type DropdownIndicatorRendererParams = ICellRendererParams<SampleSheetRowDraft, unknown> & {
  getSamplingPointDraftState?: (rowId: string) => SamplingPointDraftState | null;
};

const indicatorLabelByState: Record<SamplingPointDraftState, string> = {
  existing: 'Existing sampling point',
  new: 'New sampling point',
  'new-shared': 'New shared sampling point',
};

function hasDropdownEditor(colDef: ColDef<SampleSheetRowDraft> | undefined): boolean {
  if (!colDef) {
    return false;
  }
  if (typeof colDef.cellEditor === 'string') {
    return colDef.cellEditor === 'agSelectCellEditor' || colDef.cellEditor === 'agRichSelectCellEditor';
  }
  return colDef.cellEditor === FreeTextDropdownEditor;
}

export function DropdownIndicatorRenderer(params: DropdownIndicatorRendererParams) {
  const { value, valueFormatted, column, node, api, data, getSamplingPointDraftState } = params;
  const text =
    typeof valueFormatted === 'string'
      ? valueFormatted
      : value != null
        ? String(value)
        : '';
  const isEditable = column ? column.isCellEditable(node) : false;
  const showIcon = isEditable && column ? hasDropdownEditor(column.getColDef()) : false;
  const decoratedIndicator = typeof getSamplingPointDraftState === 'function';
  const rowId = data?.rowId;
  const isEmpty = data ? isRowEmpty(data) : false;
  const indicatorState =
    decoratedIndicator && showIcon && !isEmpty && typeof rowId === 'string' && rowId !== ''
      ? getSamplingPointDraftState(rowId)
      : null;

  const indicatorClassNames = ['rp-dd__icon-button'];
  if (decoratedIndicator) {
    indicatorClassNames.push('rp-dd__indicator');
  }
  if (indicatorState) {
    indicatorClassNames.push(`rp-dd__indicator--${indicatorState}`);
  }
  const indicatorTitle = indicatorState ? indicatorLabelByState[indicatorState] : undefined;

  const closeIfEditingSameCell = (): boolean => {
    if (!column) {
      return false;
    }
    const targetRowIndex = typeof node.rowIndex === 'number' ? node.rowIndex : undefined;
    if (typeof targetRowIndex !== 'number') {
      return false;
    }
    const rowPinned = node.rowPinned ?? undefined;
    const editorInstances = api.getCellEditorInstances({ columns: [column], rowNodes: [node] });
    if (editorInstances.length === 0) {
      return false;
    }
    api.stopEditing(false);
    api.setFocusedCell(targetRowIndex, column, rowPinned);
    return true;
  };

  const handleCellPointerDown = (event: PointerEvent<HTMLSpanElement>) => {
    if (!column) {
      return;
    }
    if (closeIfEditingSameCell()) {
      event.preventDefault();
      event.stopPropagation();
    }
  };

  const handleIndicatorPointerDown = (event: PointerEvent<HTMLSpanElement>) => {
    if (!showIcon || !column) {
      return;
    }
    if (event.button !== 0 && event.pointerType !== 'touch') {
      return;
    }
    event.preventDefault();
    event.stopPropagation();

    if (closeIfEditingSameCell()) {
      return;
    }

    const targetRowIndex = typeof node.rowIndex === 'number' ? node.rowIndex : undefined;
    if (typeof targetRowIndex !== 'number') {
      return;
    }
    const rowPinned = node.rowPinned ?? undefined;
    api.setFocusedCell(targetRowIndex, column, rowPinned);
    api.startEditingCell({ rowIndex: targetRowIndex, colKey: column, rowPinned });
  };

  return (
    <span className="rp-dd" onPointerDown={handleCellPointerDown}>
      <span className="rp-dd__text">{text}</span>
      {showIcon ? (
        <span
          role="presentation"
          className={indicatorClassNames.join(' ')}
          title={indicatorTitle}
          onPointerDown={handleIndicatorPointerDown}
          onClick={event => {
            event.preventDefault();
            event.stopPropagation();
          }}
        >
          <span className="ag-icon ag-icon-small-down rp-dd__icon" aria-hidden="true" />
        </span>
      ) : null}
    </span>
  );
}
